import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalComponentBaseProps, useModal } from '@pcmp/react-providers';
import { ModalHeader, Sheets, ModalBody } from '@ingka/modal';
import { useContentTracking } from '@/lib/hooks/tracking';
import { useModalGoToProfile } from '@/lib/hooks/modal';
import ListView, { ListViewItem } from '@ingka/list-view';
import FaqMenuSider from './faq/menu';
import ReportIssueSider from './report-issue';
import SupportSider from './support';
import UsefulLinksSider from './useful-links';
import ChangelogSider from './changelog';
import { useActiveLocation } from '@/redux/features/shopping-bag/hooks';
import ContactSider from './contact';
import { useGetAppConfigQuery } from '@/redux/back-dashboard-api';

type Section =
  | 'faq'
  | 'contact'
  | 'reportIssue'
  | 'support'
  | 'usefulLinks'
  | 'changelog';

const SIDER_SECTIONS: Record<Section, FC<ModalComponentBaseProps>> = {
  faq: FaqMenuSider,
  contact: ContactSider,
  reportIssue: ReportIssueSider,
  support: SupportSider,
  usefulLinks: UsefulLinksSider,
  changelog: ChangelogSider,
};

const HelpCenterSider: FC<ModalComponentBaseProps> = ({
  handleDismiss,
  ...rest
}) => {
  // Hooks
  const trackContent = useContentTracking();
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  const goBack = useModalGoToProfile();
  // Data
  const { data: location } = useActiveLocation();
  const { data: appConfig } = useGetAppConfigQuery(undefined);
  const contactEmail =
    location?.settings?.helpCenter.contactEmail ||
    appConfig?.appConfig?.contactEmail ||
    '';
  // Event handlers
  const goToSection = (section: Section) => async () => {
    await closeModal(0);
    await openModal({
      component: SIDER_SECTIONS[section],
      props: {},
    });
  };

  useEffect(() => {
    trackContent('help-center', { type: 'sider' });
  }, []);

  return (
    <Sheets
      {...rest}
      data-testid="helpCenterModal"
      aria-label={t('page.helpCenter.description')}
      footer={null}
      header={
        <ModalHeader
          ariaCloseTxt={t('aria.close')}
          title={t('page.helpCenter.title')}
          backBtnClick={goBack}
        />
      }
    >
      <ModalBody>
        <ListView id="help-center-list">
          <ListViewItem
            data-testid="faqBtn"
            size="medium"
            inset
            emphasised
            title={t('page.faq.title')}
            description={t('page.faq.description')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('faq')}
          />
          <ListViewItem
            data-testid="contactBtn"
            size="medium"
            inset
            emphasised
            title={t('page.contact.title')}
            description={t('page.contact.description')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('contact')}
          />
          <ListViewItem
            data-testid="reportIssueBtn"
            size="medium"
            inset
            emphasised
            title={t('page.reportIssue.title')}
            description={t('page.reportIssue.description')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('reportIssue')}
          />
          <ListViewItem
            data-testid="usefulLinksBtn"
            size="medium"
            inset
            emphasised
            title={t('page.usefulLinks.title')}
            description={t('page.usefulLinks.description')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('usefulLinks')}
          />
          <ListViewItem
            data-testid="contactAdminBtn"
            size="medium"
            inset
            emphasised
            title={t('page.helpCenter.contactAdmin.title')}
            description={t('page.helpCenter.contactAdmin.description')}
            control="link"
            controlIcon="linkOut"
            href={`mailto:${contactEmail}`}
            newWindow="blank"
          />
          <ListViewItem
            data-testid="privacyPolicyBtn"
            size="medium"
            inset
            emphasised
            title={t('page.helpCenter.privacyPolicy.title')}
            description={t('page.helpCenter.privacyPolicy.description')}
            control="link"
            controlIcon="linkOut"
            href="https://iweof.sharepoint.com/sites/ingkagroup-psr?/Documents/Data%20Privacy.pdf"
            newWindow="blank"
            className="no-underline"
          />
          {/* <ListViewItem
            data-testid="changelogBtn"
            size="medium"
            inset
            emphasised
            title={t('label.changelog.title')}
            description={t('label.changelog.description')}
            control="navigational"
            controlIcon="chevronRight"
            onClick={goToSection('changelog')}
          /> */}
        </ListView>
      </ModalBody>
    </Sheets>
  );
};

export default HelpCenterSider;
